import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useSocket } from '@/lib/socket'
import { useToast } from '@/hooks/use-toast'
import {
  Bell,
  X,
  Package,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Gift
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { Api, notificationApiUrl, profileApiUrl } from '@/services'

interface Notification {
  id: string
  type: 'order' | 'payment' | 'emergency' | 'system' | 'promotion'
  title: string
  message: string
  timestamp: string
  read: boolean
  actionUrl?: string
  actionText?: string
  priority: 'low' | 'medium' | 'high' | 'urgent'
  metadata?: {
    orderId?: string
    amount?: number
    customerName?: string
  }
  read_at?: string
}

interface NotificationCenterProps {
  isOpen: boolean
  onClose: () => void
}

export default function NotificationCenter({ isOpen, onClose }: NotificationCenterProps) {
  const { socket, isConnected } = useSocket()
  const { toast } = useToast()
  const [notifications, setNotifications] = useState<Notification[]>([])

  const { data, isLoading: loading, refetch } = useQuery({
    queryKey: ['notifications'],
    async queryFn(){
      return (await Api.get(notificationApiUrl.notifications)).data?.data
    },
    staleTime: 0.5 * 60 * 1000 // 5 minutes
  })

  useEffect(() => {
    if (isOpen) {
      setNotifications(data?.notifications || [])
    }
  }, [isOpen])

  useEffect(() => {
    if (isConnected && socket) {
      // Listen for real-time notifications
      socket.on('notification:new', handleNewNotification)
      socket.on('notification:update', handleNotificationUpdate)
      socket.on('order:new', handleNewOrderNotification)
      socket.on('order:cancelled', handleOrderCancelledNotification)
      socket.on('payment:completed', handlePaymentNotification)
      socket.on('emergency:alert', handleEmergencyNotification)

      return () => {
        socket.off('notification:new')
        socket.off('notification:update')
        socket.off('order:new')
        socket.off('order:cancelled')
        socket.off('payment:completed')
        socket.off('emergency:alert')
      }
    }
  }, [isConnected, socket])

  const handleNewNotification = (notification: Notification) => {
    setNotifications(prev => [notification, ...prev])

    // Show toast for high priority notifications
    if (notification.priority === 'high' || notification.priority === 'urgent') {
      toast({
        title: notification.title,
        description: notification.message,
        variant: notification.type === 'emergency' ? 'destructive' : 'default'
      })
    }

    // Play notification sound
    playNotificationSound(notification.priority)
  }

  const handleNotificationUpdate = (updatedNotification: Notification) => {
    setNotifications(prev =>
      prev.map(n => n.id === updatedNotification.id ? updatedNotification : n)
    )
  }

  const handleNewOrderNotification = (orderData: any) => {
    const notification: Notification = {
      id: `order_${orderData.orderId}_${Date.now()}`,
      type: 'order',
      title: 'New Order Available!',
      message: `₦${orderData.estimatedEarnings} - ${orderData.distance}km away`,
      timestamp: new Date().toISOString(),
      read: false,
      actionUrl: '/active-orders',
      actionText: 'View Order',
      priority: 'high',
      metadata: {
        orderId: orderData.orderId,
        amount: orderData.estimatedEarnings
      }
    }

    handleNewNotification(notification)
  }

  const handleOrderCancelledNotification = (orderData: any) => {
    const notification: Notification = {
      id: `order_cancelled_${orderData.orderId}_${Date.now()}`,
      type: 'order',
      title: 'Order Cancelled',
      message: `Order #${orderData.orderNumber} has been cancelled`,
      timestamp: new Date().toISOString(),
      read: false,
      priority: 'medium',
      metadata: {
        orderId: orderData.orderId
      }
    }

    handleNewNotification(notification)
  }

  const handlePaymentNotification = (paymentData: any) => {
    const notification: Notification = {
      id: `payment_${paymentData.paymentId}_${Date.now()}`,
      type: 'payment',
      title: 'Payment Received!',
      message: `₦${paymentData.amount} has been added to your wallet`,
      timestamp: new Date().toISOString(),
      read: false,
      actionUrl: '/wallet',
      actionText: 'View Wallet',
      priority: 'medium',
      metadata: {
        amount: paymentData.amount
      }
    }

    handleNewNotification(notification)
  }

  const handleEmergencyNotification = (emergencyData: any) => {
    const notification: Notification = {
      id: `emergency_${emergencyData.alertId}_${Date.now()}`,
      type: 'emergency',
      title: 'Emergency Alert',
      message: emergencyData.message || 'Emergency services have been notified',
      timestamp: new Date().toISOString(),
      read: false,
      priority: 'urgent'
    }

    handleNewNotification(notification)
  }

  const playNotificationSound = (priority: string) => {
    try {
      const audio = new Audio()

      switch (priority) {
        case 'urgent':
          audio.src = '/sounds/urgent-notification.mp3'
          break
        case 'high':
          audio.src = '/sounds/high-notification.mp3'
          break
        default:
          audio.src = '/sounds/notification.mp3'
          break
      }

      audio.volume = 0.5
      audio.play().catch(() => {
        // Ignore audio play errors (user might not have interacted yet)
      })
    } catch (error) {
      // Ignore audio errors
    }
  }

  const markAsRead = async (notificationId: string) => {
    try {
      await Api.post(notificationApiUrl.updateNotification(notificationId))
      refetch()

      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      )
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
    }
  }

  const markAllAsRead = async () => {
    try {
      await Api.post(notificationApiUrl.readAll)
      refetch()

      setNotifications(prev => prev.map(n => ({ ...n, read: true })))
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error)
    }
  }

  const deleteNotification = async (notificationId: string) => {
    try {
      await Api.delete(notificationApiUrl.updateNotification(notificationId))
      refetch()

      setNotifications(prev => prev.filter(n => n.id !== notificationId))
    } catch (error) {
      console.error('Failed to delete notification:', error)
    }
  }

  const getNotificationIcon = (type: string, priority: string) => {
    const iconClass = `h-5 w-5 ${priority === 'urgent' ? 'text-red-600' : priority === 'high' ? 'text-orange-600' : 'text-primary'}`

    switch (type) {
      case 'order':
        return <Package className={iconClass} />
      case 'payment':
        return <DollarSign className={iconClass} />
      case 'emergency':
        return <AlertTriangle className={iconClass} />
      case 'promotion':
        return <Gift className={iconClass} />
      default:
        return <Bell className={iconClass} />
    }
  }

  const getPriorityBadge = (priority: string) => {
    const colors = {
      urgent: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-primary-100 text-primary-800',
      low: 'bg-gray-100 text-gray-800'
    }

    return (
      <Badge className={colors[priority as keyof typeof colors]}>
        {priority.toUpperCase()}
      </Badge>
    )
  }

  const unreadCount = notifications.filter(n => !n.read).length

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notifications
            {unreadCount > 0 && (
              <Badge className="bg-red-500 text-white">
                {unreadCount}
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button onClick={markAllAsRead} variant="outline" size="sm">
                Mark All Read
              </Button>
            )}
            <Button onClick={onClose} variant="ghost" size="sm">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <div className="max-h-[60vh] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading notifications...</p>
                </div>
              </div>
            ) : notifications.length > 0 ? (
              <div className="divide-y">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 transition-colors ${
                      !notification.read ? 'bg-primary-50 border-l-4 border-l-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        {getNotificationIcon(notification.type, notification.priority)}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {notification.title}
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                              {notification.message}
                            </p>

                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-xs text-gray-500">
                                {new Date(notification?.read_at || '').toLocaleString()}
                              </span>
                              {getPriorityBadge(notification.priority)}
                            </div>
                          </div>

                          <div className="flex items-center gap-1 ml-2">
                            {!notification.read && (
                              <Button
                                onClick={() => markAsRead(notification.id)}
                                variant="ghost"
                                size="sm"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              onClick={() => deleteNotification(notification.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {notification.actionUrl && notification.actionText && (
                          <div className="mt-3">
                            <Button
                              onClick={() => {
                                window.location.href = notification.actionUrl!
                                onClose()
                              }}
                              size="sm"
                              variant="outline"
                            >
                              {notification.actionText}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Bell className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">No notifications yet</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}